import { type MouseEvent, useCallback, useEffect, useState } from 'react';

type UseResizeHandlerProps = {
    defaultSize: number;
    onResize: (size: number) => void;
};

export const useResizeHandler = ({ defaultSize, onResize }: UseResizeHandlerProps) => {
    const [width, setWidth] = useState(defaultSize);
    const [resizeHandlerOffset, setResizeHandlerOffset] = useState(0);
    const [isDragging, setIsDragging] = useState(false);

    useEffect(() => {
        setWidth(defaultSize);
    }, [defaultSize]);

    const onResizeHandlerClick = useCallback(
        (event: MouseEvent<HTMLDivElement>) => {
            // enable dragging state
            setIsDragging(true);

            // store the initial X position of the cursor
            const initialCursorX = event.clientX;

            const cleanUp = () => {
                window.removeEventListener('keydown', onEscapePress);
                window.removeEventListener('mouseup', onMouseUp);
                window.removeEventListener('mousemove', onMouseMove);

                // reset the mouseXOffset
                setResizeHandlerOffset(0);

                // disable dragging state
                setIsDragging(false);
            };

            const onEscapePress = (event: globalThis.KeyboardEvent) => {
                if (event.key === 'Escape') {
                    cleanUp();
                }
            };

            const onMouseMove = (event: globalThis.MouseEvent) => {
                setResizeHandlerOffset(initialCursorX - event.clientX);
            };

            const onMouseUp = (event: globalThis.MouseEvent) => {
                // remove the event listeners
                cleanUp();

                // calculate the updated width
                const updatedWidth = -1 * (initialCursorX - event.clientX);
                const safeUpdatedWidth = Math.max(64, width + updatedWidth);

                // set the updated width and pass the value to `onResize()`
                setWidth(safeUpdatedWidth);
                onResize(safeUpdatedWidth);
            };

            // add the event listeners
            window.addEventListener('keydown', onEscapePress);
            window.addEventListener('mousemove', onMouseMove);
            window.addEventListener('mouseup', onMouseUp);
        },
        [onResize, width]
    );

    return {
        isDragging,
        resizeHandlerOffset,
        width,
        onResizeHandlerClick,
    };
};
